import { getECardSlugsByTags, getCollectionSlug } from '@utils/gallery.util';

export type FilterGroups = {
  match: MarketingData;
  noMatch: MarketingData;
};

export const themeVariantFilter = {
  ///Returns a FilterGroup.
  filter(
    cards: MarketingData,
    selectedCategorySlugs: string[],
    selectedColors: string[],
    selectedLayouts: string[],
    selectedCollections: string[],
    hasPrints: boolean
  ): FilterGroups {
    if (!selectedColors.length && !selectedCategorySlugs.length && !selectedLayouts.length && !selectedCollections.length && !hasPrints) {
      return {
        match: this._sortGroupByPopularity(cards).sort((item) => (item.isPhotoECards ? -1 : 1)),
        noMatch: [],
      };
    }

    const variantsByScore = this._getVariantsByScore(
      cards,
      selectedCategorySlugs,
      selectedColors,
      selectedLayouts,
      selectedCollections,
      hasPrints
    );
    const variantsByScoreSorted = this._sortGroupsByPopularity(variantsByScore);

    const match = Array.from(variantsByScoreSorted.keys())
      .sort((a, b) => b - a)
      .reduce((acc, score) => {
        if (score === 0) {
          return acc;
        }
        const group = variantsByScore.get(score) ?? [];
        acc.push(...group);
        return acc;
      }, [] as MarketingData);

    const result = {
      match: match,
      noMatch: variantsByScoreSorted.get(0) ?? [],
    };

    return result;
  },

  _sortGroupsByPopularity(variantsByScore: Map<number, MarketingData>) {
    const result = Array.from(variantsByScore.keys()).reduce((acc, i) => {
      const group = variantsByScore.get(i) ?? [];
      const sorting = this._sortGroupByPopularity(group);
      acc.set(i, sorting);
      return acc;
    }, new Map<number, MarketingData>());
    return result;
  },

  _sortGroupByPopularity(marketingData: MarketingData) {
    const popularGroup = marketingData.filter((v) => v.tags.some((t) => t === 'Popular'));
    const unPopularGroup = marketingData.filter((v) => !v.tags.some((t) => t === 'Popular'));
    return [...popularGroup, ...unPopularGroup];
  },

  _getCollectionsSlug(selectedCollections: string[]) {
    return selectedCollections.map((collection) => getCollectionSlug(collection));
  },

  _getVariantsByScore(
    cards: MarketingData,
    selectedCategorySlugs: string[],
    selectedColors: string[],
    selectedLayouts: string[],
    selectedCollections: string[],
    hasPrints: boolean
  ) {
    const slugSet = new Set(selectedCategorySlugs);
    const colorSet = new Set(selectedColors.map((c) => c.toLowerCase()));
    const selectedLayoutSet = new Set(selectedLayouts);
    const selectedCollectionSet = new Set(this._getCollectionsSlug(selectedCollections));

    const variantsByScore = cards.reduce((acc, v) => {
      const key = this._filterScore(v, slugSet, colorSet, selectedLayoutSet, selectedCollectionSet, hasPrints);
      if (!acc.has(key)) {
        acc.set(key, []);
      }
      acc.get(key)!.push(v);
      return acc;
    }, new Map<number, MarketingData>());

    return variantsByScore;
  },

  _filterScore: (
    card: GalleryECard,
    slugSet: Set<string>,
    colorSet: Set<string>,
    variationTypes: Set<string>,
    collectionSet: Set<string>,
    hasPrints: boolean
  ) => {
    const variationMatch = () => (variationTypes.has(card.variationType) ? 1 : 0);
    const printMatch = () => (card.isJoyPrintable || card.theme?.marketingData.some((md) => md.variationType === 'prints') ? 1 : 0);
    const slugCount = () => {
      const slugs = getECardSlugsByTags(card.tags);
      const matchingSlugs = slugs.filter((s) => slugSet.has(s));
      return matchingSlugs.length;
    };
    const collectionMatch = () => (collectionSet.has(card.vendor.toLowerCase()) ? 1 : 0);
    const colorCount = () => {
      const count = card.colors.filter((c) => colorSet.has(c.toLowerCase())).length;
      const variantBump = card.variantColor && colorSet.has(card.variantColor.toLowerCase()) ? 1 : 0;
      return count + variantBump;
    };

    const criteriaByFlag = [
      { k: variationTypes.size, v: variationMatch },
      { k: hasPrints, v: printMatch },
      { k: colorSet.size, v: colorCount },
      { k: slugSet.size, v: slugCount },
      { k: collectionSet.size, v: collectionMatch },
    ];
    const criteriaScores = criteriaByFlag.filter((c) => c.k).map((c) => c.v());
    if (criteriaScores.some((c) => c === 0)) {
      return 0;
    }
    return criteriaScores.reduce((p, c) => p + c, 0);
  },
};
